import React from "react";
import { Box, useTheme } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CircularProgressBar from "../../../../../../views/components/common/progressBars/CircularProgressBar";
import { useStores } from "../../../../use-stores";
import ProductMetaChips from "../ProductMetaChips";

interface CustomerHeadingProps {
  orderId: number;
  label: string;
  moduleName: string;
  brandName?: string;
  progressValue?: number;
  quantity?: number | null;
  inventoryId?: number | null;
  inventoryType?: number | null;
  productId?: number | null;
}

const getInventoryTypeLabel = (
  inventoryType?: number | null,
): string | null => {
  if (inventoryType === 1) return "Master";
  if (inventoryType === 2 || inventoryType === 0) return "Order";
  return null;
};

const CustomerHeading: React.FC<CustomerHeadingProps> = ({
  orderId,
  label,
  moduleName,
  brandName,
  progressValue,
  quantity,
  inventoryId,
  inventoryType,
  productId,
}) => {
  const theme = useTheme();
  const { orderStore } = useStores();

  const resolvedInventoryId =
    inventoryId ?? orderStore.orderBasicDetails?.inventory_id ?? null;
  const resolvedInventoryType =
    inventoryType ?? orderStore.orderBasicDetails?.inventory_type ?? null;
  const inventoryTypeLabel = getInventoryTypeLabel(resolvedInventoryType);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 0,
        width: "100%",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          [theme.breakpoints.down("sm")]: {
            display: "block",
            alignItems: "flex-start",
            gap: 1,
          },
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                color={theme.palette.customColors.greyTextColor}
              >
                #{orderId}
              </CustomTypography>
            </Box>

            <Box>
              {label && (
                <Box
                  sx={{
                    display: "inline-block",
                    maxWidth: "100%",
                    backgroundColor: "#e4e4e4",
                    borderRadius: "16px",
                    px: 1.5,
                    py: 0.2,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  {label}
                </Box>
              )}
            </Box>

            {resolvedInventoryId != null && (
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor || "#e4e4e4",
                  borderRadius: "16px",
                  px: 1.5,
                  py: 0.2,
                }}
              >
                <CustomTypography
                  variant="heading7"
                  fontWeight={600}
                  color={theme.palette.customColors.greyTextColor}
                >
                  Inventory ID #{resolvedInventoryId}
                </CustomTypography>
              </Box>
            )}

            {inventoryTypeLabel && (
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor || "#e4e4e4",
                  borderRadius: "16px",
                  px: 1.5,
                  py: 0.2,
                }}
              >
                <CustomTypography
                  variant="heading7"
                  fontWeight={600}
                  color={theme.palette.customColors.greyTextColor}
                >
                  {inventoryTypeLabel} Inventory
                </CustomTypography>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 1 }}>
            <CustomTypography variant="heading4" fontWeight={700}>
              {moduleName}
              {!productId && quantity ? (
                <span
                  style={{ color: theme.palette.customColors.purple }}
                >{` (${quantity})`}</span>
              ) : null}
            </CustomTypography>
            {productId ? (
              <ProductMetaChips quantity={quantity} productId={productId} />
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [theme.breakpoints.down("sm")]: {
              mt: 1,
              justifyContent: "center",
            },
          }}
        >
          {progressValue ? (
            <CircularProgressBar progress={progressValue} height={60} />
          ) : null}
        </Box>
      </Box>

      {brandName && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
          <StarIcon
            sx={{
              color: theme.palette.customColors.purple,
              fontSize: "1rem",
            }}
          />
          <CustomTypography
            variant="heading7"
            fontWeight={600}
            color={theme.palette.customColors.greyTextColor}
          >
            {brandName}
          </CustomTypography>
        </Box>
      )}
    </Box>
  );
};

export default observer(CustomerHeading);
